// frontend/src/utils/swingProfile.js
//
// Swing-anchored volume profile ("support/resistance profile"): between each
// pair of consecutive swing pivots, accumulate traded volume into price bins.
// High-volume bins are prices where the market spent effort — natural
// support/resistance — and the busiest bin is the leg's Point of Control.
// Concept popularized by swing-profile studies on TradingView (BigBeluga et
// al.); this is an original implementation of the generic volume-at-price
// idea, pure candle math, fully client-side.

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function atr(candles, period) {
  const n = candles.length;
  const p = Math.min(period, n - 1);
  if (p < 1) return 0;
  let sum = 0;
  for (let i = n - p; i < n; i++) {
    const highLow = candles[i].high - candles[i].low;
    const highClose = Math.abs(candles[i].high - candles[i - 1].close);
    const lowClose = Math.abs(candles[i].low - candles[i - 1].close);
    sum += Math.max(highLow, highClose, lowClose);
  }
  return sum / p;
}

// Swing pivots via rolling-extreme completion: a bar that WAS the rolling
// max/min stops being it -> that bar is a confirmed swing point. Consecutive
// same-type pivots keep only the more extreme one, so the sequence alternates.
function detectPivots(candles, swingLen) {
  const n = candles.length;
  const pivots = [];
  const push = (type, index, price) => {
    const last = pivots[pivots.length - 1];
    if (last && last.type === type) {
      const better = type === 'high' ? price > last.price : price < last.price;
      if (better) {
        last.index = index;
        last.price = price;
      }
      return;
    }
    pivots.push({ type, index, price });
  };

  const rollMax = new Array(n);
  const rollMin = new Array(n);
  for (let i = 0; i < n; i++) {
    const from = Math.max(0, i - swingLen + 1);
    let mx = -Infinity;
    let mn = Infinity;
    for (let j = from; j <= i; j++) {
      if (candles[j].high > mx) mx = candles[j].high;
      if (candles[j].low < mn) mn = candles[j].low;
    }
    rollMax[i] = mx;
    rollMin[i] = mn;
  }

  for (let i = 1; i < n; i++) {
    if (candles[i - 1].high === rollMax[i - 1] && candles[i].high < rollMax[i]) {
      push('high', i - 1, candles[i - 1].high);
    }
    if (candles[i - 1].low === rollMin[i - 1] && candles[i].low > rollMin[i]) {
      push('low', i - 1, candles[i - 1].low);
    }
  }
  return pivots;
}

function buildLegProfile(candles, a, b, binSize) {
  const i0 = Math.min(a.index, b.index);
  const i1 = Math.max(a.index, b.index);
  const top = Math.max(a.price, b.price);
  const bottom = Math.min(a.price, b.price);
  if (!(top > bottom) || i1 - i0 < 2) return null;

  const binCount = clamp(Math.floor((top - bottom) / Math.max(binSize, 1e-9)), 4, 40);
  const step = (top - bottom) / binCount;

  const vol = new Array(binCount).fill(0);
  let buyVol = 0;
  let sellVol = 0;
  for (let i = i0; i <= i1; i++) {
    const c = candles[i];
    const v = Number(c.volume) || 0;
    const k = clamp(Math.floor((c.close - bottom) / step), 0, binCount - 1);
    vol[k] += v;
    if (c.close > c.open) buyVol += v;
    else sellVol += v;
  }

  const maxVol = Math.max(...vol);
  if (!(maxVol > 0)) return null;
  let pocBin = 0;
  for (let k = 1; k < binCount; k++) if (vol[k] > vol[pocBin]) pocBin = k;
  const total = buyVol + sellVol;

  const startPivot = a.index <= b.index ? a : b;
  const endPivot = a.index <= b.index ? b : a;

  return {
    startTime: candles[i0].time,
    endTime: candles[i1].time,
    startPrice: startPivot.price,
    endPrice: endPivot.price,
    direction: endPivot.price >= startPivot.price ? 'up' : 'down',
    bins: vol.map((v, k) => ({
      low: bottom + k * step,
      high: bottom + (k + 1) * step,
      ratio: v / maxVol,
    })),
    poc: bottom + (pocBin + 0.5) * step,
    deltaPct: total > 0 ? ((buyVol - sellVol) / total) * 100 : 0,
    totalVolume: total,
    forming: false,
  };
}

/**
 * @param {Array} candles - chart candles (numeric time, OHLCV)
 * @param {Object} opts - {swingLen: pivot window, maxLegs: completed legs kept}
 * @returns {Array} leg profiles, oldest first; the last one is the live
 *   forming leg (last pivot -> current bar) when it spans enough bars.
 */
export function computeSwingProfiles(candles, { swingLen = 50, maxLegs = 10 } = {}) {
  if (!Array.isArray(candles) || candles.length < swingLen + 10) return [];

  const binSize = atr(candles, 200) * 0.5;
  if (!(binSize > 0)) return [];

  const pivots = detectPivots(candles, swingLen);
  if (pivots.length === 0) return [];

  const profiles = [];
  for (let p = 1; p < pivots.length; p++) {
    const profile = buildLegProfile(candles, pivots[p - 1], pivots[p], binSize);
    if (profile) profiles.push(profile);
  }
  const kept = profiles.slice(-maxLegs);

  // Live forming leg: from the last confirmed pivot to the current bar's
  // most extreme price in the opposite direction.
  const lastPivot = pivots[pivots.length - 1];
  const n = candles.length;
  if (n - 1 - lastPivot.index >= 3) {
    let extremeIndex = lastPivot.index + 1;
    for (let i = lastPivot.index + 1; i < n; i++) {
      if (lastPivot.type === 'high') {
        if (candles[i].low <= candles[extremeIndex].low) extremeIndex = i;
      } else if (candles[i].high >= candles[extremeIndex].high) {
        extremeIndex = i;
      }
    }
    const formingPivot = {
      type: lastPivot.type === 'high' ? 'low' : 'high',
      index: n - 1,
      price: lastPivot.type === 'high'
        ? candles[extremeIndex].low
        : candles[extremeIndex].high,
    };
    const live = buildLegProfile(candles, lastPivot, formingPivot, binSize);
    if (live) {
      live.forming = true;
      kept.push(live);
    }
  }

  return kept;
}
